import type { FileManagerMount } from '@/components/document/sub-components/file-manager';
import { canSyncToOneDrive } from '@/composeables/file-manager-capabilities';
import type { ProjectSyncTarget } from '@/composeables/use-project-mounts';
import { notificationService } from '@/services/notification/notification-service';
import {
  classifyOneDriveFailure,
  type OneDriveFailure,
  type OneDriveFailureReason,
} from '@/services/onedrive/onedrive-sync-failure';
import {
  oneDriveSyncService,
  type OneDriveSyncOutcome,
} from '@/services/onedrive/onedrive-sync-service';
import i18n from '@/i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';

export type OneDriveSyncState =
  | 'hidden'
  | 'checking'
  | 'idle'
  | 'pending'
  | 'synced';

const oneDriveSyncKey = (target: ProjectSyncTarget | undefined) => [
  'onedrive-sync',
  target?.projectId,
  target?.title,
];

const REASON_KEYS: Record<OneDriveFailureReason, string> = {
  'consent-blocked': 'documentPane.oneDriveSync.consentFailureText',
  'signed-out': 'documentPane.oneDriveSync.failureSignedOut',
  'no-onedrive': 'documentPane.oneDriveSync.failureNoOneDrive',
  'no-access': 'documentPane.oneDriveSync.failureNoAccess',
  'name-taken': 'documentPane.oneDriveSync.failureNameTaken',
  'folder-gone': 'documentPane.oneDriveSync.failureFolderGone',
  'storage-full': 'documentPane.oneDriveSync.failureStorageFull',
  throttled: 'documentPane.oneDriveSync.failureThrottled',
  network: 'documentPane.oneDriveSync.failureNetwork',
  unknown: 'documentPane.oneDriveSync.failureText',
};

const withCode = (text: string, code: string) => `${text} [${code}]`;

const messageFor = ({
  reason,
  code,
}: OneDriveFailure): { title: string; text: string } => {
  const { t } = i18n.global;
  return {
    title: t('documentPane.oneDriveSync.failureTitle'),
    text: withCode(t(REASON_KEYS[reason]), code),
  };
};

export const describeOneDriveFailure = (
  error: unknown,
): { title: string; text: string } =>
  messageFor(classifyOneDriveFailure(error));

export const summarizeOneDriveFailures = (
  failed: OneDriveSyncOutcome['failed'],
): { title: string; text: string } | null => {
  const { t } = i18n.global;
  if (failed.length === 0) return null;

  const libraries = failed.map((entry) => entry.title).join(', ');
  const classified = failed.map((entry) =>
    classifyOneDriveFailure(entry.error),
  );
  const [first] = classified;
  const sameReason = classified.every((one) => one.reason === first.reason);
  const codes = [...new Set(classified.map((one) => one.code))];

  const scope = t('documentPane.oneDriveSync.partialFailureText', {
    libraries,
  });
  const reasonText = sameReason
    ? t(REASON_KEYS[first.reason])
    : t('documentPane.oneDriveSync.failureText');

  return {
    title: t('documentPane.oneDriveSync.failureTitle'),
    text: withCode(`${scope} ${reasonText}`, codes.join(', ')),
  };
};

export const useOneDriveSync = (
  target: MaybeRefOrGetter<ProjectSyncTarget | undefined>,
  mounts: MaybeRefOrGetter<FileManagerMount[]>,
) => {
  const { t } = i18n.global;
  const queryClient = useQueryClient();

  const currentTarget = computed(() => toValue(target));

  const canSync = computed(() => {
    const table = toValue(mounts);
    return (
      table.length > 0 &&
      table.every((mount) => canSyncToOneDrive(mount.permissions))
    );
  });

  const isPending = ref(false);

  const { data: status, isLoading: isChecking } = useQuery({
    queryKey: computed(() => oneDriveSyncKey(currentTarget.value)),
    queryFn: () =>
      oneDriveSyncService.getStatus(
        currentTarget.value!.title,
        currentTarget.value!.libraries,
      ),
    staleTime: 60_000,
    retry: false,
    enabled: computed(() => canSync.value && currentTarget.value !== undefined),
  });

  const state = computed<OneDriveSyncState>(() => {
    if (!canSync.value || !currentTarget.value) return 'hidden';
    if (isPending.value) return 'pending';
    // `isLoading`, not `isFetching`: a background refetch on refocus must not flash a spinner.
    if (isChecking.value) return 'checking';
    return status.value?.state === 'synced' ? 'synced' : 'idle';
  });

  const folderWebUrl = computed(() =>
    status.value?.state === 'synced' ? status.value.folderWebUrl : undefined,
  );

  const reportOutcome = (outcome: OneDriveSyncOutcome) => {
    const summary = summarizeOneDriveFailures(outcome.failed);

    if (summary) {
      outcome.failed.forEach((entry) =>
        console.error(`OneDrive sync failed for ${entry.title}`, entry.error),
      );
      notificationService.open({ type: 'danger', timeout: -1, ...summary });
      return;
    }

    notificationService.open({
      type: 'success',
      text: outcome.added.length
        ? t('documentPane.oneDriveSync.successText')
        : t('documentPane.oneDriveSync.alreadySyncedText'),
    });
  };

  const reportFailure = (error: unknown) => {
    const failure = classifyOneDriveFailure(error);
    notificationService.open({
      type: 'danger',
      timeout: -1,
      ...messageFor(failure),
    });
    if (failure.reason !== 'consent-blocked') {
      console.error(`OneDrive sync failed [${failure.code}]`, error);
    }
  };

  const sync = () => {
    const active = currentTarget.value;
    if (isPending.value || !active || !canSync.value) return;

    const key = oneDriveSyncKey(active);

    // Must be the FIRST statement, before any await or ref write, or the browser blocks MSAL's
    // popup.
    const run = oneDriveSyncService.syncProject(active.title, active.libraries);

    isPending.value = true;

    void run.then(
      (outcome) => {
        isPending.value = false;
        const hasShortcut =
          outcome.added.length + outcome.alreadyPresent.length ===
          active.libraries.length;
        queryClient.setQueryData(
          key,
          hasShortcut
            ? { state: 'synced', folderWebUrl: outcome.folderWebUrl }
            : { state: 'not-synced' },
        );
        reportOutcome(outcome);
      },
      (error) => {
        isPending.value = false;
        reportFailure(error);
      },
    );
  };

  return { state, folderWebUrl, sync };
};
